import { createContext, useCallback, useContext, useState } from "react";
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from "@mui/material";
import PropTypes from "prop-types";
import DsButton from "../theme/components/Button.jsx";

/**
 * Context backing useConfirm() - a real MUI Dialog in place of
 * window.confirm()/window.alert(), with the same call-site ergonomics
 * (await a promise instead of blocking the whole tab).
 */
const ConfirmContext = createContext();

/**
 * @returns {(options: {title?: string, message: string, confirmLabel?: string, cancelLabel?: string, destructive?: boolean}) => Promise<boolean>}
 */
export function useConfirm() {
  const context = useContext(ConfirmContext);
  if (!context) {
    throw new Error("useConfirm must be used within a ConfirmProvider");
  }
  return context;
}

export function ConfirmProvider({ children }) {
  const [request, setRequest] = useState(null);

  const confirm = useCallback((options) => {
    return new Promise((resolve) => {
      setRequest({ ...options, resolve });
    });
  }, []);

  function handleClose(result) {
    request?.resolve(result);
    setRequest(null);
  }

  return (
    <ConfirmContext.Provider value={confirm}>
      {children}
      <Dialog
        open={Boolean(request)}
        onClose={() => handleClose(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>{request?.title ?? "Bestätigen"}</DialogTitle>
        <DialogContent>
          <Typography variant="body2">{request?.message}</Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <DsButton variant="tertiary" onClick={() => handleClose(false)}>
            {request?.cancelLabel ?? "Abbrechen"}
          </DsButton>
          <DsButton variant="primary" onClick={() => handleClose(true)}>
            {request?.confirmLabel ?? "Bestätigen"}
          </DsButton>
        </DialogActions>
      </Dialog>
    </ConfirmContext.Provider>
  );
}

ConfirmProvider.propTypes = {
  children: PropTypes.node.isRequired,
};
